<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useFixtureStore } from '../stores/fixtureStore'
import { useCurrentUserPermissions } from '../composables/usePermissions'
import { Fixtures, Outrights } from './league'
import { useProjectionsStore } from '../stores/projections'

const { hasAnyPermission } = useCurrentUserPermissions()  


const fixtureStore = useFixtureStore()
const route = useRoute()
const projectionsStore = useProjectionsStore()
const activeMainTab = ref('fixtures')
const filters = ref({
  dateFrom: '',
  dateTo: '',
  status: 'All Status',
  league_id: null
})

const leagues = ref([])

// Get the current league info
const currentLeague = computed(() => {
  const leagueId = route.params.id
  if (!leagues.value.length || !leagueId) return null
  
  return leagues.value.find(league => {
    const id = league.id || league.league_id
    return id?.toString() === leagueId.toString()
  })
})

const currentLeagueName = computed(() => {

  return currentLeague.value?.league_name || `League ${route.params.id}`
})

onMounted(async () => {
  projectionsStore.selectedLeague = currentLeague.value
  // Fetch leagues for current league name display
  leagues.value = await fixtureStore.getLeagues()
  
  // Set the league_id filter from the route parameter
  filters.value.league_id = route.params.id
})

// Watch for route changes to update league filter
watch(() => route.params.id, (newLeagueId) => {
  if (newLeagueId) {
    filters.value.league_id = newLeagueId
  }
}, { immediate: true })

</script>

<template>
  <div>
    <!-- Header -->
    <div  class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold">Scheduled Games</h1>
        <p class="text-gray-500">Dashboard / Fixtures </p>
        <p class="text-gray-500">League: {{ fixtureStore.leagues?.find(league => league.id == route.params.id)?.league_name }}  </p>
      </div>
      

      <div  v-if="hasAnyPermission(['fixtures.publishOdds'])" class=" hidden flex gap-3">
        <button class="btn-secondary">
          <i class="fas fa-filter mr-2"></i> Filter
        </button>
        <button class="btn-secondary">
          <i class="fas fa-download mr-2"></i> Export
        </button>

        <button class="btn-primary bg-pink-500 hover:bg-pink-600">
          + Create Fixture
        </button>
      </div>
    </div>

    <!-- Fixtures/Outrights Tabs -->
    <div class="mb-6">
      <div class="flex border-b border-gray-200">
        <button
          @click="activeMainTab = 'fixtures'"
          class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
          :class="activeMainTab === 'fixtures' 
            ? 'border-pink-500 text-pink-600 bg-pink-50' 
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
        >
          Fixtures
        </button>
        <button
          @click="activeMainTab = 'outrights'"
          class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
          :class="activeMainTab === 'outrights' 
            ? 'border-pink-500 text-pink-600 bg-pink-50' 
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
        >
          Outrights
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <!-- <div class="grid grid-cols-3 gap-6 mb-8">
      <StatCard
        title="Total Fixtures"
        :value="stats.totalFixtures.value"
        :change="stats.totalFixtures.change"
        period="vs last month"
        icon="calendar"
      />
      <StatCard
        title="Today's Games"
        :value="stats.todaysGames.value"
        :change="stats.todaysGames.change"
        period="vs yesterday"
        icon="basketball"
      />
      <StatCard
        title="This Month"
        :value="stats.thisMonth.value"
        :change="stats.thisMonth.change"
        period="vs last month"
        icon="chart-bar"
      />
    </div> -->

    <!-- Fixtures Tab Content -->
    <Fixtures 
      v-if="activeMainTab === 'fixtures'"
      :current-league-name="currentLeagueName"
      :filters="filters"
    />

    <!-- Outrights Tab Content -->
    <Outrights 
      v-if="activeMainTab === 'outrights'"
      :current-league-name="currentLeagueName"
    />
  </div>
</template>

<style scoped>
.btn-primary {
  @apply px-4 py-2 text-white rounded-lg transition-colors duration-200;
}

.btn-secondary {
  @apply px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg 
         hover:bg-gray-50 transition-colors duration-200;
}
</style> 